import {
  Button,
  Checkbox,
  Classes,
  Icon,
  Intent,
  Menu,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Switch
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Tooltip2 } from "@blueprintjs/popover2";
import { faPeopleArrows } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format, isAfter, startOfDay } from "date-fns";
import { ButtonContainer, IDataTableColumn, showError, ToolTipButton } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";

import { SuiviReassignmentDialog } from "..";
import {
  DashboardApi,
  DashboardSuiviSearch,
  EDashboardSuiviMode,
  EIfapmeSide,
  EOrigineSuivi,
  SuiviGridDto
} from "../../../api";
import { ERoutes } from "../../../AppRouter";
import { CustomBulletList, LinkButton, SearchTablePage, ViewButton } from "../../../components";
import { useAuth, useDialog, useEventsContext } from "../../../contexts";
import { useAbortableApiServiceFactory, useApiService, useTabMessage, useTheme, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";
import { Guid } from "../../../utils/guid";

const LegendHeaderContainer = styled.div`
  & > * + * {
    margin-left: 0.5rem;
  }
`;

const StyledSwitch = styled(Switch)`
  & .${Classes.SWITCH} {
    &:hover {
      input:checked ~ .${Classes.CONTROL_INDICATOR} {
        background: ${props => props.color};
      }
    }
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PopoverContent = styled.div`
  padding: 0.5rem;
`;

export interface IDashboardSuivisProps {}

export const DashboardSuivis: React.FunctionComponent<IDashboardSuivisProps> = props => {
  const { t } = useTl();
  const { user, hasPermission } = useAuth();
  const { ifapmeSide, theme } = useTheme();
  const api = useApiService(DashboardApi);
  const apiFactory = useAbortableApiServiceFactory(DashboardApi);
  const lastAbortController = React.useRef<AbortController>();
  const [selectedIDs, setSelectedIDs] = React.useState<number[]>([]);

  const [mode, setMode] = React.useState<EDashboardSuiviMode>(EDashboardSuiviMode.ARealiser);
  const [displayHidden, setDisplayHidden] = React.useState(false);
  const searchFunction = React.useCallback(
    (sObj?: DashboardSuiviSearch) => {
      sObj.ifapmeSide = ifapmeSide === "walter" ? EIfapmeSide.Walter : EIfapmeSide.Hope;
      sObj.hideMasquees = !displayHidden;
      sObj.mode = mode;
      const { api: abortableApi, abortController } = apiFactory();
      lastAbortController.current = abortController;
      return abortableApi.dashboardSearchSuivis({ DashboardSuiviSearch: sObj });
    },
    [apiFactory, displayHidden, ifapmeSide, mode]
  );

  const [rowLoading, setRowLoading] = React.useState(null);

  const { showDialogPromise } = useDialog();
  const { dispatchEvent } = useEventsContext();
  const marquerCommeFait = React.useCallback(
    async (idsuivi: number, origin: EOrigineSuivi) => {
      const res = await showDialogPromise({
        title: t(ETLCodes.MarquerSuiviCommeFaitConfirmTitle),
        message: t(ETLCodes.MarquerSuiviCommeFaitConfirmMessage)
      });
      if (res === "yes") {
        setRowLoading({ idsuivi, origin });
        await api.dashboardMarquerSuiviFait({
          idsuivi
        });
        setRowLoading(null);
        dispatchEvent("SEARCH_TABLE_REFRESH");
        dispatchEvent("DASHBOARD_COUNTS_REFRESH_Suivis");
      }
    },
    [api, dispatchEvent, showDialogPromise, t]
  );
  const masquerSuivi = React.useCallback(
    async (idsuivi: number, origin: EOrigineSuivi) => {
      const res = await showDialogPromise({
        title: t(ETLCodes.MasquerSuiviConfirmTitle),
        message: t(ETLCodes.MasquerSuiviConfirmMessage)
      });
      if (res === "yes") {
        setRowLoading({ idsuivi, origin });
        await api.dashboardMasquerSuivi({
          idsuivi
        });
        setRowLoading(null);
        dispatchEvent("SEARCH_TABLE_REFRESH");
        dispatchEvent("DASHBOARD_COUNTS_REFRESH_Suivis");
      }
    },
    [api, dispatchEvent, showDialogPromise, t]
  );

  const toggleSelection = React.useCallback(
    (item: SuiviGridDto) => {
      if (!selectedIDs.includes(item.idsuivi)) {
        setSelectedIDs(ids => [...ids, item.idsuivi]);
      } else {
        setSelectedIDs(ids => ids.filter(id => id !== item.idsuivi));
      }
    },
    [selectedIDs]
  );

  const getActionValue = React.useCallback((row: SuiviGridDto) => {
    if (!row.actionDateRealisation && !row.actionDateEcheance) return null;
    return format(!!row.actionDateRealisation ? row.actionDateRealisation : row.actionDateEcheance, "dd-MM-yyyy");
  }, []);

  const identifier = React.useMemo(() => Guid.newGuid(), []);
  const { openTabThenSendMessage } = useTabMessage(identifier);

  const onPersonneClick = React.useCallback((row: SuiviGridDto) => {
    let url = "";

    if (row.idapprenant) url = `#${ERoutes.apprenant}/${row.idapprenant}/signaletique/edit`;
    if (row.idrepresentant) url = `#${ERoutes.representant}/${row.idrepresentant}/detail/edit`;
    if (row.idformateur) url = `#${ERoutes.formateur}/${row.idformateur}/signaletique/edit`;
    if (row.idtuteur) url = `#${ERoutes.tuteur}/${row.idtuteur}/detail/edit`;
    if (row.idcontact) url = `#${ERoutes.contacts}/${row.idcontact}/detail/edit`;
    if (row.idchefEntreprise) url = `#${ERoutes.chefEntreprise}/${row.idchefEntreprise}/detail/edit`;

    window.open(url, "_blank");
  }, []);

  const getForme = React.useCallback((row: SuiviGridDto) => {
    if (row.idapprenant) return ETLCodes.Apprenant;
    if (row.idrepresentant) return ETLCodes.Representant;
    if (row.idformateur) return ETLCodes.Formateur;
    if (row.idtuteur) return ETLCodes.Tuteur;
    if (row.idcontact) return ETLCodes.Contact;
    if (row.idchefEntreprise) return ETLCodes.ChefEntreprise;
  }, []);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        fieldName: "checkboxes",
        autoFitContent: true,
        render: (item: SuiviGridDto) => (
          <Checkbox
            checked={selectedIDs.includes(item.idsuivi)}
            onChange={e => {
              toggleSelection(item);
            }}
          />
        )
      },
      {
        computed: true,
        fieldName: "actions",
        autoFitContent: true,
        render: (row: SuiviGridDto) => (
          <ButtonContainer>
            {user.iduser === row.realisateurAction && (
              <ToolTipButton
                tooltipPosition="bottom"
                minimal
                icon="tick"
                onClick={() => marquerCommeFait(row.idsuivi, row.origineSuivi)}
                loading={!!rowLoading && rowLoading.idsuivi === row.idsuivi && rowLoading.origin === row.origineSuivi}
                disabled={!!rowLoading && rowLoading !== row.idsuivi}
                tooltip={t(ETLCodes.MarquerCommeFait)}
              />
            )}
            {!row.masquee && !!row.actionDateEcheance && isAfter(new Date(), row.actionDateEcheance) && (
              <ToolTipButton
                tooltipPosition="bottom"
                tooltip={t(ETLCodes.MasquerSuivi)}
                minimal
                icon="cross"
                onClick={() => masquerSuivi(row.idsuivi, row.origineSuivi)}
                loading={!!rowLoading && rowLoading.idsuivi === row.idsuivi && rowLoading.origin === row.origineSuivi}
                disabled={!!rowLoading && rowLoading !== row.idsuivi}
              />
            )}
            <ViewButton
              minimal={true}
              onClick={() =>
                openTabThenSendMessage(
                  row.fromHope
                    ? `#${ERoutes.suivi}/${row.idsuivi}/detail/view/hope`
                    : `#${ERoutes.suivi}/${row.idsuivi}/detail/view/walter`,
                  "FromDashboard"
                )
              }
            />
          </ButtonContainer>
        )
      },
      {
        header: () => (
          <LegendHeaderContainer>
            <span>{t(ETLCodes.ActionARealiser)}</span>
            <Tooltip2
              content={
                <CustomBulletList
                  title={t(ETLCodes.Legende)}
                  items={[
                    {
                      text: t(ETLCodes.ActionRealisee),
                      color: theme.sucessColor
                    },
                    {
                      text: t(ETLCodes.DateEcheanceDepassee),
                      color: theme.dangerColor
                    },
                    {
                      text: t(ETLCodes.ActionNonRealisee),
                      color: theme.warningColor
                    }
                  ]}
                />
              }
              popoverClassName="bullet-list-popover"
              position="right"
            >
              <Icon icon="info-sign" style={{ cursor: "pointer" }} />
            </Tooltip2>
          </LegendHeaderContainer>
        ),
        fieldName: "actionARealiser",
        render: (row: SuiviGridDto) => (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              color: !!row.actionDateRealisation
                ? theme.sucessColor
                : isAfter(startOfDay(new Date()), startOfDay(row.actionDateEcheance))
                ? theme.dangerColor
                : theme.warningColor
            }}
          >
            <Icon
              style={{ marginRight: ".5rem" }}
              icon={
                !!row.actionDateRealisation
                  ? "tick"
                  : isAfter(startOfDay(new Date()), startOfDay(row.actionDateEcheance))
                  ? "issue"
                  : "time"
              }
            />
            {getActionValue(row)}
          </span>
        )
      },
      {
        header: () => t(ETLCodes.Source),
        fieldName: "source",
        render: (row: SuiviGridDto) =>
          row.origineSuivi === EOrigineSuivi.Services
            ? row.service
            : row.origineSuivi === EOrigineSuivi.Centres
            ? row.centre
            : t(ETLCodes.Smile)
      },
      {
        header: () => t(ETLCodes.DateSuivi),
        fieldName: "dateSuivi"
      },
      {
        header: () => t(ETLCodes.ResponsableAction),
        fieldName: "responsableAction",
        render: (row: SuiviGridDto) => (
          <LinkButton
            text={row.responsableAction}
            onClick={() => window.open(`#${ERoutes.users}/${row.realisateurAction}/detail/view`, "_blank")}
          />
        ),
        hidden: mode === EDashboardSuiviMode.ARealiser
      },
      {
        header: () => t(ETLCodes.RealisateurSuivi),
        fieldName: "realisateurSuivi",
        render: (row: SuiviGridDto) => (
          <LinkButton
            text={row.realisateurSuivi}
            onClick={() => window.open(`#${ERoutes.users}/${row.idrealisateurSuivi}/detail/view`, "_blank")}
          />
        ),
        hidden: mode === EDashboardSuiviMode.Demande
      },
      {
        header: () => t(ETLCodes.Encodeur),
        fieldName: "encodeur",
        render: (row: SuiviGridDto) => (
          <LinkButton
            text={row.encodeur}
            onClick={() => window.open(`#${ERoutes.users}/${row.idencodeur}/detail/view`, "_blank")}
          />
        ),
        hidden: mode === EDashboardSuiviMode.Demande
      },
      {
        header: () => t(ETLCodes.Forme),
        fieldName: "forme",
        render: (row: SuiviGridDto) => t(getForme(row))
      },
      {
        header: () => t(ETLCodes.Beneficiaire),
        fieldName: "personne",
        render: (row: SuiviGridDto) => <LinkButton text={row.personne} onClick={() => onPersonneClick(row)} />
      },
      {
        header: () => t(ETLCodes.TypeSuivi),
        fieldName: "typeSuivi"
      },
      {
        header: () => t(ETLCodes.SousTypeSuivi),
        fieldName: "sousTypeSuivi"
      }
    ],
    [
      getActionValue,
      getForme,
      marquerCommeFait,
      masquerSuivi,
      mode,
      onPersonneClick,
      openTabThenSendMessage,
      rowLoading,
      selectedIDs,
      t,
      theme.dangerColor,
      theme.sucessColor,
      theme.warningColor,
      toggleSelection,
      user.iduser
    ]
  );

  const getCriteria = React.useCallback(async () => {
    return await api.dashboardGetSuivisCriterias();
  }, [api]);

  const onAbort = React.useCallback(() => lastAbortController.current?.abort(), []);

  const [reassignmentDialogOpen, setReassignmentDialogOpen] = React.useState(false);
  const reassign = React.useCallback(() => {
    if (selectedIDs.length <= 0) {
      showError(t(ETLCodes.SelectAtLeastOne));
      return;
    }

    setReassignmentDialogOpen(true);
  }, [selectedIDs.length, t]);

  const onCloseReassignment = React.useCallback(() => {
    setReassignmentDialogOpen(false);
    dispatchEvent("SEARCH_TABLE_REFRESH");
  }, [dispatchEvent]);

  const rightElement = React.useMemo(
    () => (
      <ButtonsContainer>
        {hasPermission("SUIVI", "RW", "REASSIGNMENT") && (
          <Popover
            position="bottom"
            content={
              <PopoverContent>
                <Menu>
                  <MenuItem
                    icon={<FontAwesomeIcon icon={faPeopleArrows} />}
                    text={t(ETLCodes.ReattribuerSuivi)}
                    intent={Intent.PRIMARY}
                    onClick={() => reassign()}
                  />
                </Menu>
              </PopoverContent>
            }
          >
            <Button
              icon={IconNames.CHEVRON_DOWN}
              text={t(ETLCodes.GlobalActions)}
              minimal={true}
              intent={Intent.PRIMARY}
            ></Button>
          </Popover>
        )}
        <StyledSwitch
          color={theme.primaryColor}
          label={t(ETLCodes.AfficherMasquees)}
          checked={displayHidden}
          onChange={e => {
            setDisplayHidden(e.currentTarget.checked);
          }}
        ></StyledSwitch>
      </ButtonsContainer>
    ),
    [displayHidden, hasPermission, reassign, t, theme.primaryColor]
  );

  return (
    <>
      <RadioGroup
        selectedValue={mode}
        inline
        onChange={e => setMode(e.currentTarget.value as EDashboardSuiviMode.ARealiser)}
      >
        <Radio label={t(ETLCodes.ARealiser)} value={EDashboardSuiviMode.ARealiser} />
        <Radio label={t(ETLCodes.Demandé)} value={EDashboardSuiviMode.Demande} />
        <Radio label={t(ETLCodes.ASuivre)} value={EDashboardSuiviMode.ASuivre} />
      </RadioGroup>
      <SearchTablePage
        searchStateInitialSearch={false}
        withCard={false}
        columns={columns}
        getCriteriasFunction={getCriteria}
        searchFunction={searchFunction}
        onAbort={onAbort}
        sortKeys={{ dateSuivi: "DESC" }}
        rightElement={rightElement}
      />
      {reassignmentDialogOpen && (
        <SuiviReassignmentDialog
          dialogOpen={reassignmentDialogOpen}
          onClose={onCloseReassignment}
          idsSuivi={selectedIDs}
        />
      )}
    </>
  );
};
